import React, { useState, useEffect } from 'react';
import { auth, db } from './firebase';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider, updateProfile } from 'firebase/auth';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import './EditProfileModal.css';

function EditProfileModal({ user, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    leagueName: '',
    city: '',
    state: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPasswordSection, setShowPasswordSection] = useState(false);

  // Password strength validation (same as Login.js)
  const [passwordStrength, setPasswordStrength] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false
  });

  useEffect(() => {
    loadUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadUserData = async () => {
    try {
      // Get user data from Firestore
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setFormData({
          name: userData.name || '',
          phone: userData.phone || '',
          leagueName: userData.leagueName || '',
          city: userData.city || '',
          state: userData.state || '',
          email: user.email || '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (pwd) => {
    const strength = {
      minLength: pwd.length >= 8,
      hasUpperCase: /[A-Z]/.test(pwd),
      hasLowerCase: /[a-z]/.test(pwd),
      hasNumber: /[0-9]/.test(pwd),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(pwd)
    };
    setPasswordStrength(strength);
    return Object.values(strength).every(req => req === true);
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setFormData({ ...formData, newPassword });
    validatePassword(newPassword);
  };

  const handleConfirmPasswordChange = (e) => {
    setFormData({ ...formData, confirmPassword: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const currentUser = auth.currentUser;

      // Update name and phone in Firestore
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        name: formData.name,
        phone: formData.phone,
        leagueName: formData.leagueName,
        city: formData.city,
        state: formData.state
      });

      // Update display name in Firebase Auth
      await updateProfile(currentUser, {
        displayName: formData.name
      });

      // Update password if provided
      if (formData.newPassword) {
        // Validate current password is provided
        if (!formData.currentPassword) {
          setError('Current password required to change password');
          setSaving(false);
          return;
        }

        // Validate new password strength
        if (!validatePassword(formData.newPassword)) {
          setError('New password does not meet security requirements');
          setSaving(false);
          return;
        }

        // Validate passwords match
        if (formData.newPassword !== formData.confirmPassword) {
          setError('New passwords do not match');
          setSaving(false);
          return;
        }

        // Reauthenticate
        const credential = EmailAuthProvider.credential(
          user.email,
          formData.currentPassword
        );
        await reauthenticateWithCredential(currentUser, credential);

        // Update password
        await updatePassword(currentUser, formData.newPassword);
      }

      alert('Profile updated successfully!');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating profile:', error);
      
      // Handle specific error messages
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setError('Current password is incorrect');
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak');
      } else if (error.code === 'auth/requires-recent-login') {
        setError('Please log out and log back in before changing password');
      } else {
        setError('Failed to update profile: ' + error.message);
      }
      
      setSaving(false);
      return;
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content edit-profile-modal">
          <h2>Edit Profile</h2>
          <div className="loading">Loading your profile...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content edit-profile-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Edit Profile</h2>
        <p>Update your account information</p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Basic Information</h3>
            
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Your name"
              />
            </div>

            <div className="form-group">
              <label>Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Your phone number"
              />
            </div>

            <div className="form-group">
              <label>League/School Name *</label>
              <input
                type="text"
                value={formData.leagueName}
                onChange={(e) => setFormData({ ...formData, leagueName: e.target.value })}
                required
                placeholder="e.g., Westside Little League"
              />
            </div>

            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                required
                placeholder="e.g., Chicago"
              />
            </div>

            <div className="form-group">
              <label>State *</label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                required
                placeholder="e.g., IL"
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                disabled
                placeholder="Your email"
              />
              <small className="field-hint">To change your email, contact support@warriorwalkupmusic.com</small>
            </div>
          </div>

          <div className="form-section">
            <div className="password-section-header">
              <h3>Change Password</h3>
              <button
                type="button"
                className="toggle-password-btn"
                onClick={() => setShowPasswordSection(!showPasswordSection)}
              >
                {showPasswordSection ? 'Cancel' : 'Change Password'}
              </button>
            </div>

            {showPasswordSection && (
              <div className="password-fields">
                <div className="form-group">
                  <label>Current Password *</label>
                  <input
                    type="password"
                    value={formData.currentPassword}
                    onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                    required={showPasswordSection}
                    placeholder="Enter current password"
                  />
                </div>

                <div className="form-group">
                  <label>New Password *</label>
                  <input
                    type="password"
                    value={formData.newPassword}
                    onChange={handlePasswordChange}
                    required={showPasswordSection}
                    placeholder="Enter new password"
                  />
                </div>

                {formData.newPassword && (
                  <div className="password-requirements">
                    <p className="requirements-title">Password must contain:</p>
                    <ul>
                      <li className={passwordStrength.minLength ? 'met' : 'unmet'}>
                        {passwordStrength.minLength ? '✓' : '○'} At least 8 characters
                      </li>
                      <li className={passwordStrength.hasUpperCase ? 'met' : 'unmet'}>
                        {passwordStrength.hasUpperCase ? '✓' : '○'} One uppercase letter
                      </li>
                      <li className={passwordStrength.hasLowerCase ? 'met' : 'unmet'}>
                        {passwordStrength.hasLowerCase ? '✓' : '○'} One lowercase letter
                      </li>
                      <li className={passwordStrength.hasNumber ? 'met' : 'unmet'}>
                        {passwordStrength.hasNumber ? '✓' : '○'} One number
                      </li>
                      <li className={passwordStrength.hasSpecialChar ? 'met' : 'unmet'}>
                        {passwordStrength.hasSpecialChar ? '✓' : '○'} One special character (!@#$%^&*)
                      </li>
                    </ul>
                  </div>
                )}

                <div className="form-group">
                  <label>Confirm New Password *</label>
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleConfirmPasswordChange}
                    required={showPasswordSection}
                    placeholder="Re-enter new password"
                  />
                  {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
                    <div className="password-mismatch-error">
                      ❌ Passwords do not match
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="modal-buttons">
            <button type="submit" className="submit-btn" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditProfileModal;